import axios from "axios";
import { getCookie } from "../../../helper/cookieHelper";
import { ACCESS_TOKEN, USER_DETAILS } from "../../constants";
import { encryptJson } from "../../utils/react-ec-utils";
import {
  CheckInBookingDetails,
  WaitTimeRestaurantDetail,
  CancelCheckInDetails,
} from "./checkInModels";
import API from "../../Api";

// V3 API instance for new reservation backend (plain JSON, no encryption)
const API_V3 = axios.create({
  baseURL: process.env.REACT_APP_API_ENDPOINT_V3,
  timeout: 60000,
});

API_V3.interceptors.request.use((config) => {
  const token = getCookie(ACCESS_TOKEN);
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const getUserDetailsFromCookie = () => getCookie(USER_DETAILS) || {};

// Making Booking
export function makeBooking(CheckInDetails: CheckInBookingDetails) {
  const data = encryptJson(CheckInDetails);

  return API({
    method: "post",
    url: "/api/customers/checkIn/fetch",
    params: { data },
  });
}

// Get WaitTime
export function getWaitTimes(detail: WaitTimeRestaurantDetail) {
  const payload = { locationId: detail.locationID };
  const data = encryptJson(payload);
  return API({
    method: "post",
    url: `/merchants/v2/locations/wait-times`,
    data: data,
  });
}

// Cancel Booking
export function cancelBooking(CheckInDetails: CancelCheckInDetails) {
  // Keep V3 flow for staging reservation-revamp branch behavior.
  if (process.env.REACT_APP_API_ENDPOINT_V3) {
    const payload = { status: "Y", cancelReason: CheckInDetails?.cancelReason };
    return API_V3({
      method: "PATCH",
      // url: `/reservations/${CheckInDetails.reservationID}/status`,
      url: `/customer-app/reservations/${CheckInDetails.reservationID}/status`,
      data: payload,
    });
  }

  // Legacy fallback (keeps source-branch compatibility where V3 is unavailable).
  const userDetails = getUserDetailsFromCookie();
  const payload = {
    locationId: CheckInDetails.locationID,
    reservationId: CheckInDetails.reservationID,
    cancelReason: CheckInDetails?.cancelReason,
    emailId: userDetails?.email || "",
    mobileNo: userDetails?.mobilePhone || "",
  };
  const data = encryptJson(payload);
  return API({
    method: "put",
    url: `/api/customers/reservation/cancel-table?user=customer`,
    data: { data },
  });
}

// Update Booking
export function updateBooking(CheckInDetails: CheckInBookingDetails) {
  return API({
    method: "post",
    url: "/merchants/" + CheckInDetails.locationId + "/reservations",
    data: CheckInDetails,
  });
}

// Legacy booking details call
export function getBooking(reservationID: string) {
  const payload = { reservationId: reservationID };
  const data = encryptJson(payload);
  return API({
    method: "post",
    url: "/api/customers/checkIn/reservationId",
    data: { data },
  });
}

export function getSessionApi(data: any) {
  if (process.env.REACT_APP_API_ENDPOINT_V3) {
    return API_V3({
      method: "get",
      // url: `/reservations/slots?locationId=${data.locationId}&date=${data.Date}&guestCount=${data.GuestSize}`,
      url: `/customer-app/reservations/slots?locationId=${data.locationId}&date=${data.Date}&guestCount=${data.GuestSize}`,
    });
  }

  return API({
    method: "get",
    url: `/api/customers/reservation/generate-reservation-slot/${data.locationId}?guestCount=${data.GuestSize}&date=${data.Date}`,
  });
}

export function makeReservationApi(ReservationDetails: CheckInBookingDetails) {
  const data = encryptJson(ReservationDetails);

  return API({
    method: "post",
    url: "/api/customers/checkIn/fetch",
    params: { data },
  });
}

// V3: Create Reservation / Waitlist via new backend (plain JSON)
export function makeReservationApiV3(payload: any) {
  return API_V3({
    method: "post",
    //url: "/reservations",
    url: "/customer-app/reservations",
    data: payload,
  });
}

// V3: Get Reservation Details by ID
export function getBookingV3(reservationId: string) {
  return API_V3({
    method: "get",
    // url: `/reservations/${reservationId}`,
    // url: `/customer-app/reservations/${reservationId}`,
    url: `/customer-app/reservations/${reservationId}/queue-status`,
  });
}

// V3: Get waitlist queue count for a location
export function getWaitlistCountsV3(locationId: string) {
  return API_V3({
    method: "get",
    url: `/reservations/counts?locationId=${locationId}`,
  });
}

// V3: Get waitlist entries to calculate position
// export function getWaitlistEntriesV3(locationId: string) {
//   return API_V3({
//     method: "get",
//     url: `/customer-app/reservations?locationId=${locationId}&isReservation=0&status=C&limit=100`,
//   });
// }

// V3: Get reservation config (reservationDayLimit, maxPartySizeOnline, etc.)
export function getReservationConfigApi(locationId: string) {
  return API_V3({
    method: "get",
    // url: `locations/${locationId}/config/RESERVATION_CONFIG`,
    url: `customer-app/locations/${locationId}/config/RESERVATION_CONFIG`,
  });
}

// V3: Lookup customer by phone + location
export function lookupCustomerApi(phone: string, locationId: string) {
  return API_V3({
    method: "get",
    url: `/customers/lookup`,
    params: { phone, locationId },
  });
}
